import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Star, ThumbsUp, AlertTriangle, Gift, StickyNote, Trash2, Plus,
  Car, Cake, Check, Save, User, Phone, MapPin, ShieldAlert, Send, FileText,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { fmtDate, fmtDateTime } from "@/lib/datetime";
import CustomerTimeline from "./CustomerTimeline";
import AdminDocUpload, { DocFile } from "./AdminDocUpload";

function CustomerDocs({ token, email }) {
  const [docs, setDocs] = useState(null);
  const load = () => axios.get(`${API}/admin-docs/customer/${encodeURIComponent(email)}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setDocs(r.data.docs)).catch(() => setDocs([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [email]);
  return (
    <div className="rounded-lg bg-[#0C0C0C] border border-white/10 p-4" data-testid="customer-docs">
      <h4 className="text-sm font-semibold text-white/80 flex items-center gap-2 mb-3"><FileText size={15} className="text-[#F2B705]" /> Documents on File</h4>
      {!docs ? <Loader2 size={14} className="animate-spin text-white/40" />
        : docs.length === 0 ? <p className="text-white/40 text-xs mb-3">No documents on file yet — upload the first one below.</p> : (
        <div className="flex flex-wrap gap-3 mb-3">
          {docs.map((d, i) => <DocFile key={i} path={d.path} label={d.label} token={token} sub={d.source} />)}
        </div>
      )}
      <div className="pt-3 border-t border-white/5">
        <AdminDocUpload entityType="customer" entityId={email} token={token} onUploaded={load} />
        <p className="text-white/30 text-[10px] mt-1.5">Documents uploaded here go into this customer's Vault and auto-attach to their future reservation requests.</p>
      </div>
    </div>
  );
}

const CREDIT_TYPES = [["free_day", "Free Day(s)"], ["dollar", "Dollar Off ($)"], ["percent", "Percent Off (%)"]];
const field = "w-full px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm focus:border-[#F2B705] outline-none [color-scheme:dark]";

export default function CustomerDetail({ token, customer, onChanged }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const enc = encodeURIComponent(customer.email);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ name: "", phone: "", address: "", preferred_vehicle: "", birthday: "", rating: 0, great_customer: false, problem_customer: false });
  const [savingProfile, setSavingProfile] = useState(false);
  const [note, setNote] = useState("");
  const [credit, setCredit] = useState({ type: "free_day", amount: 1, reason: "" });
  const [pendingVerifs, setPendingVerifs] = useState([]);
  const [nudging, setNudging] = useState(null);

  const loadPending = () => {
    axios.get(`${API}/system/pending-verifications?email=${enc}`, auth)
      .then((r) => setPendingVerifs(r.data?.items || [])).catch(() => setPendingVerifs([]));
  };

  const nudge = async (p) => {
    setNudging(`${p.booking_id}:${p.type}`);
    try {
      const { data } = await axios.post(`${API}/system/pending-verifications/nudge`,
        { booking_id: p.booking_id, type: p.type }, auth);
      if (data.suppressed) toast.warning("Nudge logged, but SMS Notifications automation is OFF.");
      else toast.success(`Nudge text sent to ${p.customer}!`);
      loadPending();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send nudge"); }
    finally { setNudging(null); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/customers/${enc}/detail`, auth);
      setData(data);
      setProfile((p) => ({
        ...p, ...data.profile,
        name: data.profile.name || customer.name || "",
        phone: data.profile.phone || customer.phone || "",
        address: data.profile.address || "",
      }));
      loadPending();
    } catch { toast.error("Failed to load customer detail"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customer.email]);

  const saveProfile = async (patch) => {
    const next = { ...profile, ...patch };
    setProfile(next);
    setSavingProfile(true);
    try {
      const { data } = await axios.patch(`${API}/customers/${enc}/profile`, patch, auth);
      const s = data.synced || {};
      if (s.bookings || s.reservations || s.crm) {
        toast.success(`Saved & synced everywhere — ${s.bookings || 0} booking(s), ${s.reservations || 0} reservation(s)${s.crm ? " + CRM contact" : ""} updated.`);
      }
      onChanged?.();
    } catch { toast.error("Could not save"); }
    finally { setSavingProfile(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await axios.post(`${API}/customers/${enc}/notes`, { text: note.trim() }, auth);
      setNote(""); toast.success("Note added"); load(); onChanged?.();
    } catch { toast.error("Could not add note"); }
  };
  const delNote = async (id) => {
    try { await axios.delete(`${API}/customers/${enc}/notes/${id}`, auth); load(); onChanged?.(); } catch { toast.error("Failed"); }
  };

  const grantCredit = async () => {
    try {
      await axios.post(`${API}/customers/${enc}/credits`, { type: credit.type, amount: Number(credit.amount) || 1, reason: credit.reason.trim() }, auth);
      setCredit({ type: "free_day", amount: 1, reason: "" });
      toast.success("Credit added"); load(); onChanged?.();
    } catch { toast.error("Could not add credit"); }
  };
  const redeemCredit = async (id) => {
    try { await axios.post(`${API}/customers/${enc}/credits/${id}/redeem`, {}, auth); toast.success("Marked used"); load(); onChanged?.(); }
    catch { toast.error("Failed"); }
  };
  const delCredit = async (id) => {
    try { await axios.delete(`${API}/customers/${enc}/credits/${id}`, auth); load(); onChanged?.(); } catch { toast.error("Failed"); }
  };

  if (loading || !data) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#F2B705]" size={18} /></div>;

  const availableCredits = data.credits.filter((c) => c.status === "available");
  const usedCredits = data.credits.filter((c) => c.status !== "available");

  return (
    <div className="space-y-5" data-testid={`customer-profile-${customer.email}`}>
      {/* Pending verifications */}
      {pendingVerifs.length > 0 && (
        <div className="rounded-lg bg-amber-400/[0.06] border border-amber-400/40 p-4" data-testid="customer-pending-verifications">
          <h4 className="text-sm font-semibold text-amber-400 flex items-center gap-2 mb-2">
            <ShieldAlert size={15} /> Pending Verifications ({pendingVerifs.length})
          </h4>
          <div className="space-y-2">
            {pendingVerifs.map((p, i) => (
              <div key={i} data-testid={`pending-verif-${i}`} className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-white/90 font-semibold">
                      {p.what} <span className="text-amber-400 font-normal">— pending {p.pending_for}</span>
                    </div>
                    <div className="text-white/45 text-xs mt-0.5">{p.vehicle}{p.last_message ? ` · ${p.last_message}` : ""}</div>
                    <div className="text-[#F2B705]/85 text-xs mt-0.5">→ {p.action}</div>
                  </div>
                  <button data-testid={`nudge-btn-${p.type}-${i}`} onClick={() => nudge(p)} disabled={nudging === `${p.booking_id}:${p.type}`}
                    className="shrink-0 flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-md bg-[#F2B705] text-black hover:bg-white transition-colors disabled:opacity-50">
                    {nudging === `${p.booking_id}:${p.type}` ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />} Nudge
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents on File + admin upload */}
      <CustomerDocs token={token} email={customer.email} />

      {/* Drivers Club (points, tier, DOB, address) */}
      <ClubSection token={token} email={customer.email} />

      {/* Contact info (editable) */}
      <div className="rounded-lg bg-[#0C0C0C] border border-white/10 p-4" data-testid="customer-contact">
        <h4 className="text-sm font-semibold text-white/80 flex items-center gap-2 mb-3"><User size={15} className="text-[#F2B705]" /> Contact Info</h4>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-white/45 flex items-center gap-1 mb-1"><User size={12} /> Name</label>
            <input className={field} value={profile.name || ""} data-testid="contact-name-input"
              onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
              onBlur={(e) => saveProfile({ name: e.target.value })} placeholder="Full name" />
          </div>
          <div>
            <label className="text-[11px] text-white/45 flex items-center gap-1 mb-1"><Phone size={12} /> Phone</label>
            <input className={field} value={profile.phone || ""} data-testid="contact-phone-input"
              onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
              onBlur={(e) => saveProfile({ phone: e.target.value })} placeholder="(555) 555-5555" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] text-white/45 flex items-center gap-1 mb-1"><MapPin size={12} /> Address</label>
            <input className={field} value={profile.address || ""} data-testid="contact-address-input"
              onChange={(e) => setProfile((p) => ({ ...p, address: e.target.value }))}
              onBlur={(e) => saveProfile({ address: e.target.value })} placeholder="Street, City, State ZIP" />
          </div>
        </div>
        <p className="text-white/30 text-[11px] mt-2">Email <span className="text-white/50">{customer.email}</span> is the customer key and can't be changed here.</p>
      </div>

      {/* Profile card */}
      <div className="rounded-lg bg-[#0C0C0C] border border-white/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-white/80 flex items-center gap-2">Renter Profile {savingProfile && <Loader2 size={12} className="animate-spin text-white/40" />}</h4>
          <div className="flex items-center gap-1" data-testid="customer-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => saveProfile({ rating: n })} data-testid={`rating-star-${n}`}>
                <Star size={16} className={n <= (profile.rating || 0) ? "text-[#F2B705] fill-[#F2B705]" : "text-white/25"} />
              </button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-white/45 flex items-center gap-1 mb-1"><Car size={12} /> Preferred Vehicle</label>
            <input className={field} value={profile.preferred_vehicle || ""} data-testid="pref-vehicle-input"
              onChange={(e) => setProfile((p) => ({ ...p, preferred_vehicle: e.target.value }))}
              onBlur={(e) => saveProfile({ preferred_vehicle: e.target.value })} placeholder="e.g. Kia Rio" />
          </div>
          <div>
            <label className="text-[11px] text-white/45 flex items-center gap-1 mb-1"><Cake size={12} /> Birthday</label>
            <input type="date" className={field} value={profile.birthday || ""} data-testid="birthday-input"
              onChange={(e) => saveProfile({ birthday: e.target.value })} />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button data-testid="flag-great" onClick={() => saveProfile({ great_customer: !profile.great_customer })}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${profile.great_customer ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-white/5 text-white/50 border-white/10"}`}>
            <ThumbsUp size={13} /> Great Customer
          </button>
          <button data-testid="flag-problem" onClick={() => saveProfile({ problem_customer: !profile.problem_customer })}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${profile.problem_customer ? "bg-red-500/20 text-red-300 border-red-500/40" : "bg-white/5 text-white/50 border-white/10"}`}>
            <AlertTriangle size={13} /> Problem Customer
          </button>
        </div>
      </div>

      {/* Credits */}
      <div className="rounded-lg bg-[#0C0C0C] border border-[#F2B705]/25 p-4" data-testid="customer-credits">
        <h4 className="text-sm font-semibold text-white/80 flex items-center gap-2 mb-3"><Gift size={15} className="text-[#F2B705]" /> Credits & Comps
          {availableCredits.length > 0 && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#F2B705] text-black font-bold">{availableCredits.length} owed</span>}
        </h4>
        <div className="grid sm:grid-cols-[1fr,90px,1.4fr,auto] gap-2 items-end mb-3">
          <div>
            <label className="text-[11px] text-white/45 block mb-1">Type</label>
            <select className={field} value={credit.type} data-testid="credit-type-select" onChange={(e) => setCredit((c) => ({ ...c, type: e.target.value }))}>
              {CREDIT_TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-white/45 block mb-1">Amount</label>
            <input type="number" className={field} value={credit.amount} data-testid="credit-amount-input" onChange={(e) => setCredit((c) => ({ ...c, amount: e.target.value }))} />
          </div>
          <div>
            <label className="text-[11px] text-white/45 block mb-1">Reason</label>
            <input className={field} value={credit.reason} data-testid="credit-reason-input" onChange={(e) => setCredit((c) => ({ ...c, reason: e.target.value }))} placeholder="Why are you comping them?" />
          </div>
          <button data-testid="credit-grant-btn" onClick={grantCredit} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-semibold h-[38px]"><Plus size={14} /> Grant</button>
        </div>
        {data.credits.length === 0 ? <p className="text-white/35 text-sm">No credits yet.</p> : (
          <div className="space-y-1.5" data-testid="credits-list">
            {availableCredits.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 bg-[#F2B705]/[0.07] border border-[#F2B705]/25 rounded px-3 py-2 text-sm">
                <span><span className="font-bold text-[#F2B705]">{c.label}</span>{c.reason ? ` · ${c.reason}` : ""} <span className="text-white/35 text-xs">· {fmtDate(c.created_at)}</span></span>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`credit-redeem-${c.id}`} onClick={() => redeemCredit(c.id)} className="flex items-center gap-1 text-xs border border-emerald-400/40 text-emerald-400 px-2.5 py-1 rounded hover:bg-emerald-500 hover:text-white"><Check size={12} /> Mark Used</button>
                  <button onClick={() => delCredit(c.id)} className="text-red-400/70 hover:text-red-400"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
            {usedCredits.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 bg-black/40 border border-white/10 rounded px-3 py-2 text-sm opacity-60">
                <span className="line-through"><span className="font-semibold">{c.label}</span>{c.reason ? ` · ${c.reason}` : ""}</span>
                <span className="text-white/40 text-xs">used {fmtDate(c.used_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="rounded-lg bg-[#0C0C0C] border border-white/10 p-4" data-testid="customer-notes">
        <h4 className="text-sm font-semibold text-white/80 flex items-center gap-2 mb-3"><StickyNote size={15} className="text-sky-400" /> Private Admin Notes</h4>
        <div className="flex gap-2 mb-3">
          <textarea rows={2} className={field} value={note} data-testid="note-input" onChange={(e) => setNote(e.target.value)} placeholder="e.g. Cancelled 7/1 — car was down for the holiday, owe them a free day." />
          <button data-testid="note-add-btn" onClick={addNote} className="flex items-center gap-1.5 px-4 rounded-md bg-sky-500 text-white text-sm font-semibold self-stretch"><Plus size={14} /></button>
        </div>
        {data.notes.length === 0 ? <p className="text-white/35 text-sm">No notes yet.</p> : (
          <div className="space-y-1.5" data-testid="notes-list">
            {data.notes.map((n) => (
              <div key={n.id} className="flex items-start justify-between gap-2 bg-black/40 border border-white/10 rounded px-3 py-2 text-sm">
                <div><div className="text-white/85">{n.text}</div><div className="text-white/35 text-xs mt-0.5">{n.author} · {fmtDateTime(n.created_at)}</div></div>
                <button onClick={() => delNote(n.id)} className="text-red-400/70 hover:text-red-400 shrink-0"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Full customer history timeline */}
      <CustomerTimeline token={token} email={customer.email} />
    </div>
  );
}

function ClubSection({ token, email }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/customers/${encodeURIComponent(email)}/club`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setD(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [email]);
  if (!d || !d.club) return null;
  const c = d.club;
  return (
    <div className="rounded-lg bg-[#0C0C0C] border border-[#F2B705]/25 p-4" data-testid="customer-club-section">
      <h4 className="text-sm font-semibold text-white/80 mb-2">🏁 Drivers Club — <span className="text-[#F2B705]">{c.tier}</span></h4>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div><div className="text-white/40">Redeemable Points</div><div data-testid="club-balance" className="text-emerald-300 font-bold text-base">{Number(c.balance).toLocaleString()}</div></div>
        <div><div className="text-white/40">Lifetime Qualifying</div><div data-testid="club-lifetime" className="text-white font-bold text-base">{Number(c.lifetime).toLocaleString()}</div></div>
        <div><div className="text-white/40">DOB (admin only)</div><div className="text-white/80">{d.profile.dob || "—"}</div></div>
        <div><div className="text-white/40">Address</div><div className="text-white/80 truncate" title={d.profile.address}>{d.profile.address || "—"}</div></div>
      </div>
      {c.next_tier && (
        <div className="mt-2">
          <div className="text-[11px] text-white/45">{Number(c.next_tier.needed).toLocaleString()} points to {c.next_tier.name}</div>
          <div className="h-1.5 bg-white/10 rounded-full mt-1"><div className="h-1.5 rounded-full bg-[#F2B705]" style={{ width: `${Math.min(100, c.next_tier.progress_pct)}%` }} /></div>
        </div>
      )}
      <button data-testid="club-activity-toggle" onClick={() => setOpen(!open)} className="mt-2 text-[11px] text-white/50 hover:text-white">{open ? "Hide" : "Show"} points activity ({c.activity.length})</button>
      {open && (
        <div className="mt-1 divide-y divide-white/5 max-h-48 overflow-y-auto" data-testid="club-activity-list">
          {c.activity.map((e, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 text-[11px] gap-2">
              <span className="text-white/55 min-w-0 truncate">{e.reason}<span className="text-white/25"> · {(e.at || "").slice(0, 16).replace("T", " ")} CT · {e.actor}</span></span>
              <span className={`font-bold shrink-0 ${e.points >= 0 ? "text-emerald-300" : "text-red-300"}`}>{e.points >= 0 ? "+" : ""}{Number(e.points).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
